import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router";

import { recover } from "../../api/auth";
import { ApiError, errorText } from "../../api/client";
import { Button } from "../../components/Button";
import { nl } from "../../i18n/nl";
import { AuthLayout, Field, FormError } from "./AuthLayout";
import styles from "./auth.module.css";
import { useRefreshSession } from "./session";

const t = nl.auth;

export function RecoverPage() {
  const navigate = useNavigate();
  const refresh = useRefreshSession();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim() || !code.trim()) {
      setError(t.required);
      return;
    }
    setBusy(true);
    setError(null);
    setPasswordError(null);
    try {
      await recover(email, code, password || null);
      await refresh();
      void navigate("/", { replace: true });
    } catch (failure) {
      if (failure instanceof ApiError && failure.code === "weak_password") {
        setPasswordError(errorText(failure));
      } else {
        setError(errorText(failure));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <h1 className={styles.title}>{t.recover.title}</h1>
      <p className={styles.lead}>{t.recover.text}</p>
      <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <FormError message={error} />
        <Field
          label={t.email}
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={t.emailPlaceholder}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <Field
          label={t.recover.code}
          autoComplete="one-time-code"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={t.recover.codePlaceholder}
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
        <Field
          label={t.newPassword}
          type="password"
          autoComplete="new-password"
          help={t.recover.newPasswordHelp}
          value={password}
          error={passwordError}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
        <Button type="submit" variant="primary" size="large" className={styles.full} disabled={busy}>
          {busy ? t.working : t.recover.submit}
        </Button>
      </form>
      <p className={styles.help}>{t.recover.noCode}</p>
      <div className={styles.links}>
        <Link to="/inloggen">{t.recover.backToLogin}</Link>
      </div>
    </AuthLayout>
  );
}
